(() => {
  if (window.__pillarLoaded) return;
  window.__pillarLoaded = true;

  // ---------- pillar-progress-tabs ----------
  class PillarProgressTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-tab-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
      if (!this.tabs.length) return;
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.tabTrigger));
      });
      this.activate(this.tabs[0].dataset.tabTrigger);
    }
    activate(id) {
      this.tabs.forEach((tab) => {
        tab.classList.toggle('is-active', tab.dataset.tabTrigger === id);
        tab.setAttribute('aria-selected', tab.dataset.tabTrigger === id);
      });
      this.panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.tabPanel === id);
      });
    }
  }
  customElements.define('pillar-progress-tabs', PillarProgressTabs);

  // ---------- pillar-piece-toggle ----------
  // active tab 写到 host 的 data-active-tab="a"|"b"，CSS 据此切换列显隐。
  class PillarPieceToggle extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-piece-tab]'));
      if (!this.tabs.length) return;
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.pieceTab));
      });
      if (!this.dataset.activeTab) this.activate(this.tabs[0].dataset.pieceTab);
    }
    activate(id) {
      this.dataset.activeTab = id;
      this.tabs.forEach((tab) => {
        const on = tab.dataset.pieceTab === id;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }
  }
  customElements.define('pillar-piece-toggle', PillarPieceToggle);

  // ---------- carousel nav helpers (stop-based scrolling) ----------
  // iOS Safari 对 scrollBy + mandatory snap 的程序化 smooth scroll 不可靠：
  // 落到非 snap 位置时不会回拉，于是按钮点击会在边界附近停在错位。
  // 解法：永远 scrollTo 到一个明确 stop（每个 cell 的 snap 起点 + 0 + maxScroll）。
  function readPadX(el) {
    const v = parseFloat(getComputedStyle(el).scrollPaddingInlineStart);
    return Number.isFinite(v) ? v : 0;
  }
  function buildStops(track) {
    const padX = readPadX(track);
    const trackLeft = track.getBoundingClientRect().left;
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    const stops = new Set([0, max]);
    for (const cell of track.children) {
      const pos = cell.getBoundingClientRect().left - trackLeft + track.scrollLeft - padX;
      stops.add(Math.max(0, Math.min(max, Math.round(pos))));
    }
    return [...stops].sort((a, b) => a - b);
  }
  function pickStop(stops, current, want, dir) {
    let target = null;
    if (dir > 0) {
      // 往前：取 want 范围内最大的 stop（走得最远）
      for (const s of stops) {
        if (s > current + 1 && s <= want + 1) target = s;
      }
      if (target == null) {
        for (const s of stops) {
          if (s > current + 1) { target = s; break; }
        }
      }
    } else {
      // 往后：取 want 范围内最小的 stop（同样走得最远——往后即数值最小）。
      // 之前用反向迭代 + break 取的是最大（最接近 current）的 stop，导致 next
      // 一次能跨多卡到末位，prev 一次只走一卡，方向不对称。
      for (const s of stops) {
        if (s < current - 1 && s >= want - 1) { target = s; break; }
      }
      if (target == null) {
        for (let i = stops.length - 1; i >= 0; i--) {
          if (stops[i] < current - 1) { target = stops[i]; break; }
        }
      }
    }
    return target;
  }
  function navScroll(track, dir, step) {
    const stops = buildStops(track);
    // 把 current 吸附到最近的 stop。scrollLeft 落在两卡之间（pin 模式遗留、
    // 浏览器 scroll restore、touch 中途松手等）时，距离驱动的 step 会一边
    // 跨过下一卡、一边够不到上一卡，造成 next 一下、prev 两下的不对称。
    const current0 = track.scrollLeft;
    let nearest = stops[0];
    let minDist = Math.abs(stops[0] - current0);
    for (let i = 1; i < stops.length; i++) {
      const d = Math.abs(stops[i] - current0);
      if (d < minDist) { minDist = d; nearest = stops[i]; }
    }
    const current = nearest;
    const want = current + dir * step;
    const target = pickStop(stops, current, want, dir);
    if (target == null) return;
    track.scrollTo({ left: target, behavior: 'smooth' });
  }

  // ---------- carousel base (shared by reviews + pain cards) ----------
  class PillarCarouselBase extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track) return;
      const step = () => this.track.clientWidth * 0.8;
      this.onPrev = () => navScroll(this.track, -1, step());
      this.onNext = () => navScroll(this.track, +1, step());
      this.prev && this.prev.addEventListener('click', this.onPrev);
      this.next && this.next.addEventListener('click', this.onNext);

      this.updateNavState = () => {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const atStart = scrollLeft <= 1;
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
        if (this.prev) this.prev.disabled = atStart;
        if (this.next) this.next.disabled = atEnd;
      };
      this.track.addEventListener('scroll', this.updateNavState, { passive: true });
      window.addEventListener('resize', this.updateNavState);
      requestAnimationFrame(this.updateNavState);
    }
    disconnectedCallback() {
      if (!this.track) return;
      if (this.prev && this.onPrev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next && this.onNext) this.next.removeEventListener('click', this.onNext);
      if (this.updateNavState) {
        this.track.removeEventListener('scroll', this.updateNavState);
        window.removeEventListener('resize', this.updateNavState);
      }
    }
  }
  customElements.define('pillar-reviews-carousel', class extends PillarCarouselBase {});
  customElements.define('pillar-pain-carousel', class extends PillarCarouselBase {});
  customElements.define('pillar-fits-carousel', class extends PillarCarouselBase {});

  // ---------- pillar-progress: 滚动驱动 pin（桌面端） ----------
  // 当外层 [data-pillar-pin="true"] 包裹存在时，纵向 scroll progress 映射成
  // track 的 scrollLeft；移动端 / reduced-motion / 单卡 自动跳过。
  class PillarProgressCarousel extends PillarCarouselBase {
    connectedCallback() {
      super.connectedCallback();
      this.maybeInitPin();
    }
    disconnectedCallback() {
      this.teardownPin();
      super.disconnectedCallback();
    }
    maybeInitPin() {
      this.pinWrap = this.closest('[data-pillar-pin]');
      if (!this.pinWrap || this.pinWrap.dataset.pillarPin !== 'true') return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!this.track) return;

      this.mql = window.matchMedia('(min-width: 768px)');
      this.onMQ = () => {
        if (this.mql.matches) this.activatePin();
        else this.deactivatePin();
      };
      // Safari < 14 用旧 API
      if (this.mql.addEventListener) this.mql.addEventListener('change', this.onMQ);
      else if (this.mql.addListener) this.mql.addListener(this.onMQ);
      this.onMQ();
    }
    activatePin() {
      if (this.pinActive) return;
      this.pinActive = true;
      document.body.classList.add('pillar-pin-ready');
      this.classList.add('is-pinned');
      this._lastIndex = -1;
      this.onTick = () => this.scheduleTick();
      window.addEventListener('scroll', this.onTick, { passive: true });
      window.addEventListener('resize', this.onTick);
      // 同步算一次目标 index 并打 .is-active，避免 next-frame 之前出现“全部 opacity:0”空白帧。
      this.applyProgress();
    }
    deactivatePin() {
      if (!this.pinActive) return;
      this.pinActive = false;
      this.classList.remove('is-pinned');
      window.removeEventListener('scroll', this.onTick);
      window.removeEventListener('resize', this.onTick);
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      // 退出 pin（resize 到 mobile）：清掉 is-active，复位 scrollLeft，避免堆叠态遗留导致 stacked 布局看不见卡。
      this._lastIndex = undefined;
      if (this.track) {
        Array.from(this.track.children).forEach((card) => card.classList.remove('is-active'));
        this.track.scrollLeft = 0;
      }
    }
    scheduleTick() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.applyProgress();
      });
    }
    applyProgress() {
      if (!this.pinActive || !this.pinWrap || !this.track) return;
      const cards = this.track.children;
      const cardCount = cards.length;
      if (cardCount <= 1) return;
      const rect = this.pinWrap.getBoundingClientRect();
      const range = rect.height - window.innerHeight;
      if (range <= 0) return;
      let progress = -rect.top / range;
      if (progress < 0) progress = 0;
      else if (progress > 1) progress = 1;
      // Snap-to-card：纵向 progress 离散成 0..N-1 索引。pin 期间卡片不再横向滚动，
      // 而是 grid 同位堆叠，靠 .is-active 的 opacity 渐入切换图片+文字。
      const targetIndex = Math.round(progress * (cardCount - 1));
      if (this._lastIndex === targetIndex) return;
      this._lastIndex = targetIndex;
      for (let i = 0; i < cardCount; i++) {
        cards[i].classList.toggle('is-active', i === targetIndex);
      }
    }
    teardownPin() {
      this.deactivatePin();
      if (this.mql && this.onMQ) {
        if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.onMQ);
        else if (this.mql.removeListener) this.mql.removeListener(this.onMQ);
      }
    }
  }
  customElements.define('pillar-progress-carousel', PillarProgressCarousel);

  // ---------- pillar-carousel-row (pillar-training carousel_mode 内嵌) ----------
  class PillarCarouselRow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-row-track]');
      this.prev = this.querySelector('[data-row-prev]');
      this.next = this.querySelector('[data-row-next]');
      if (!this.track) return;
      const step = () => Math.max(this.track.clientWidth * 0.6, 240);
      this.onPrev = () => navScroll(this.track, -1, step());
      this.onNext = () => navScroll(this.track, +1, step());
      this.prev && this.prev.addEventListener('click', this.onPrev);
      this.next && this.next.addEventListener('click', this.onNext);

      this.updateNavState = () => {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const atStart = scrollLeft <= 1;
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
        if (this.prev) this.prev.disabled = atStart;
        if (this.next) this.next.disabled = atEnd;
      };
      this.track.addEventListener('scroll', this.updateNavState, { passive: true });
      window.addEventListener('resize', this.updateNavState);
      requestAnimationFrame(this.updateNavState);
    }
    disconnectedCallback() {
      if (!this.track) return;
      if (this.prev && this.onPrev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next && this.onNext) this.next.removeEventListener('click', this.onNext);
      if (this.updateNavState) {
        this.track.removeEventListener('scroll', this.updateNavState);
        window.removeEventListener('resize', this.updateNavState);
      }
    }
  }
  customElements.define('pillar-carousel-row', PillarCarouselRow);

  // ---------- pillar-product-form ----------
  // 不复用 <product-form>：product.js 的 .single-add-to-cart-button 全局查询
  // 会让每张 pillar 卡同时进入 loading 态。这里每张卡独立 form + button。
  const cartSectionsToRender = [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' },
  ];

  const getSectionInnerHTML = (html, selector) => {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : '';
  };

  class PillarProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.button = this.querySelector('button[type="submit"]');
      if (!this.form || !this.button) return;
      this.onSubmitBound = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmitBound);
    }
    disconnectedCallback() {
      if (this.form && this.onSubmitBound) {
        this.form.removeEventListener('submit', this.onSubmitBound);
      }
    }
    onSubmit(evt) {
      evt.preventDefault();
      if (this.button.classList.contains('loading') || this.button.disabled) return;
      this.button.classList.add('loading');
      this.button.setAttribute('aria-disabled', 'true');
      this.clearError();

      const formData = new FormData(this.form);
      formData.append('sections', cartSectionsToRender.map((s) => s.section).join(','));
      formData.append('sections_url', window.location.pathname);

      const cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';

      fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData,
      })
        .then(async (r) => {
          // Shopify 失败时（如 sold out）返回 4xx + JSON 错误体；先解析再分支。
          let data;
          try {
            data = await r.json();
          } catch (_) {
            throw new Error('cart_add_response_not_json');
          }
          if (!r.ok || data.status) {
            const msg = data && (data.description || data.message);
            throw new Error(msg || 'cart_add_failed');
          }
          return data;
        })
        .then((data) => {
          if (data.sections) {
            cartSectionsToRender.forEach(({ id, section, selector }) => {
              const host = document.getElementById(id);
              if (!host || !data.sections[section]) return;
              const target = host.querySelector(selector) || host;
              target.innerHTML = getSectionInnerHTML(data.sections[section], selector);
            });
            if (typeof Cart !== 'undefined') {
              try { new Cart().renderContents({ sections: data.sections }); } catch (_) {}
            }
          }

          const drawer = document.getElementById('Cart-Drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:item-added', {
              bubbles: true,
              detail: { product: data.items ? data.items[0] : data },
            })
          );
        })
        .catch((e) => {
          console.error('[pillar-product] add-to-cart failed:', e);
          this.showError(e && e.message);
        })
        .finally(() => {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-disabled');
        });
    }
    showError(msg) {
      const fallback =
        (window.theme && window.theme.variantStrings && window.theme.variantStrings.unavailable) ||
        'Could not add to cart. Please try again.';
      const text = msg && msg !== 'cart_add_failed' && msg !== 'cart_add_response_not_json'
        ? msg
        : fallback;
      let host = this.querySelector('.pillar-product__error');
      if (!host) {
        host = document.createElement('p');
        host.className = 'pillar-product__error';
        host.setAttribute('role', 'alert');
        host.setAttribute('aria-live', 'polite');
        this.appendChild(host);
      }
      host.textContent = text;
    }
    clearError() {
      const host = this.querySelector('.pillar-product__error');
      if (host) host.textContent = '';
    }
  }
  customElements.define('pillar-product-form', PillarProductForm);

  // ---------- reveal helper（独立于主题 settings.animations） ----------
  // 默认 [data-animate] 可见；JS 检测通过后注入 body.pillar-anim-ready
  // 切到隐藏初态再由 IO 揭示。无 JS / 无 IO / reduce-motion 自动兜底。
  const initReveal = () => {
    // theme editor 会对单个 section 局部 re-render，新节点不会被这里的 IO 观察到，
    // 但 body.pillar-anim-ready 仍在 → 全部停在 opacity:0。design mode 下直接跳过。
    if (window.Shopify && window.Shopify.designMode) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    document.body.classList.add('pillar-anim-ready');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -15% 0px' });
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
  };

  // ---------- training 视频可见性驱动 autoplay ----------
  // 用 IO 替代原生 autoplay：离屏暂停省电，进屏自动播放。
  const initLazyVideos = () => {
    const videos = document.querySelectorAll('video[data-pillar-lazy-video]');
    if (!videos.length) return;
    if (!('IntersectionObserver' in window)) {
      videos.forEach((v) => v.play().catch(() => {}));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        if (entry.isIntersecting) {
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
    }, { rootMargin: '50px 0px', threshold: 0.25 });
    videos.forEach((v) => observer.observe(v));
  };


  const onReady = () => { initReveal(); initLazyVideos(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
